import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import logo from '../../../components/layout/logo_new.png';
import MessageHandler from '../../../components/core/MessageHandler';
import { setAuthToken } from '../../../services/getAuthToken';
import { cohortEnrollBegin, cohortEnrollConfirm } from '../api/mfa.api';

interface RouterState {
  challengeToken?: string;
  cohort?: string;
}

// Cohort-forced enrolment. Reached from /login when the server answers with
// enrolmentRequired instead of a session: the user's cohort switch-on date has
// passed and they have not set up two-step login yet, so they cannot get in
// until they do.
//
// Deliberately not reusing SecuritySettings' enrolment dialog: that one runs
// inside an authenticated session and calls /mfa/enroll/*. Here there is no
// session at all — the enrol challenge token is the only credential, and
// confirm-cohort mints the session as part of the same request.
const CohortEnrolment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const state = location.state as RouterState | null;
  const challengeToken = state?.challengeToken ?? '';
  const cohort = state?.cohort ?? '';

  const [provisionalSecret, setProvisionalSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const expired = useCallback(() => {
    setError(t('mfa.cohort_error_expired'));
    window.setTimeout(() => navigate('/login', { replace: true }), 1800);
  }, [navigate, t]);

  useEffect(() => {
    // Landing here directly (no router state) means there is no challenge to
    // enrol against — send them back to sign in properly.
    if (!challengeToken) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const begun = await cohortEnrollBegin(challengeToken);
        if (cancelled) return;
        setProvisionalSecret(begun.provisionalSecret);
        setQrDataUrl(begun.qrDataUrl);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AxiosError && err.response?.status === 401) {
          expired();
        } else {
          setError(t('mfa.enrol_error_qr_failed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [challengeToken, navigate, expired, t]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(t('mfa.enrol_error_empty'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const session = await cohortEnrollConfirm(
        challengeToken,
        provisionalSecret,
        code.trim()
      );
      // confirm-cohort returns a session too, so the user lands inside the app
      // rather than being sent back through the login screen they just passed.
      await setAuthToken(session.accessToken);
      setRecoveryCodes(session.recoveryCodes);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 401) {
        expired();
        return;
      }
      const message =
        err instanceof AxiosError
          ? err.response?.data?.message ?? t('mfa.enrol_error_bad_code')
          : t('mfa.enrol_error_bad_code');
      setError(String(message));
    } finally {
      setSubmitting(false);
    }
  };

  // Recovery codes are shown once, after a successful enrolment. Continuing
  // is a deliberate click so the codes cannot be missed by an auto-redirect.
  if (recoveryCodes.length > 0) {
    return (
      <div className="bg-login-background bg-cover">
        <div className="h-screen flex items-center justify-center">
          <div className="bg-white border-grey-input_border border-2 shadow-lg rounded-2xl flex flex-col py-8 px-12 space-y-5 max-w-sm">
            <img className="mx-auto p-6" src={logo} alt="School's Cool" />
            <h1 className="text-lg font-semibold text-grey-dark">
              {t('mfa.codes_title')}
            </h1>
            <p className="text-sm text-grey-medium">{t('mfa.codes_intro')}</p>
            <div className="bg-grey-light rounded-lg p-3 font-mono text-sm space-y-1">
              {recoveryCodes.map((c) => (
                <p key={c}>{c}</p>
              ))}
            </div>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-lg bg-sky text-white px-2 shadow-md py-1 hover:bg-sky-dark transition-colors"
            >
              {t('mfa.done')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-login-background bg-cover">
      <div className="h-screen flex items-center justify-center">
        <form
          onSubmit={handleSubmit}
          className="bg-white border-grey-input_border border-2 shadow-lg rounded-2xl flex flex-col justify-around py-8 px-12 space-y-5 max-w-sm"
        >
          <img className="mx-auto p-6" src={logo} alt="School's Cool" />

          <div className="flex flex-col space-y-1">
            <h1 className="text-lg font-semibold text-grey-dark">
              {t('mfa.cohort_title')}
            </h1>
            <p className="text-sm text-grey-medium">
              {cohort
                ? t('mfa.cohort_intro_named', { cohort })
                : t('mfa.cohort_intro')}
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-grey-medium">{t('mfa.cohort_loading')}</p>
          ) : (
            qrDataUrl && (
              <>
                <img
                  className="mx-auto"
                  src={qrDataUrl}
                  alt={t('mfa.enrol_qr_alt')}
                  width={180}
                  height={180}
                />
                <p className="text-xs text-grey-medium break-all">
                  {t('mfa.enrol_manual_key')} <strong>{provisionalSecret}</strong>
                </p>

                <div className="flex flex-col space-y-1">
                  <label className="text-sm text-grey-medium">
                    {t('mfa.enrol_code_label')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="placeholder-grey-medium text-sm rounded-lg shadow-md border-none py-2 px-3"
                    placeholder={t('mfa.enrol_code_placeholder')}
                  />
                </div>

                <button
                  disabled={submitting || !code.length}
                  type="submit"
                  className="rounded-lg bg-sky text-white px-2 shadow-md py-1 hover:bg-sky-dark disabled:hover:bg-sky transition-colors"
                >
                  {submitting
                    ? t('mfa.second_step_verifying')
                    : t('mfa.confirm')}
                </button>
              </>
            )
          )}

          <MessageHandler error={error} success="" />
        </form>
      </div>
    </div>
  );
};

export default CohortEnrolment;
